/**
 * THE STRUCTURAL ACTION LOG — one component, two chromes.
 *
 * This was two files (StructuralActionLogModal, StructuralActionLogPage) that were ~90%
 * identical: the same fetch, the same poll, the same revert call, the same card list, the
 * same dialogs. Only the frame around them differed — a modal overlay on the reconciliation
 * page, a full page from Settings.
 *
 * Duplication in a revert UI is not a tidiness problem. Everything the server now guarantees
 * has to be *rendered* to be true, and a duplicated renderer means each fix lands once and is
 * missed once: the confirmation step, the verbatim server message, reading `isLocked` from
 * the API instead of recomputing it. The two copies had already drifted (only one told its
 * host to refresh after a revert). So the behaviour lives here exactly once and the frame is
 * a prop.
 *
 * WHAT THIS COMPONENT DELIBERATELY DOES NOT DO
 * --------------------------------------------
 *   - It never decides what is locked or revertable. `isLocked` / `isRevertable` come from
 *     the server, which orders by sequence_id. The UI used to sort the same rows by a tied
 *     timestamp and could disagree with the server about which action was newest.
 *   - It never rewrites a server error. Preconditions are now written for people ("An item
 *     named X was created after this action — rename it first"), and are shown as sent.
 *   - It never reverts on a single click. Reverting rewrites live inventory; the confirmation
 *     names what is about to change.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import http from '../services/httpClient';
import { onDataChanged, onScreenResumed } from '../utils/dataChangeSignal';
import { revertErrorMessage } from '../utils/revertErrorMessage';
import { revertSummary } from '../utils/structuralActionSnapshot';
import StructuralActionDetails from './StructuralActionDetails';
import './StructuralActionLogModal.css';

// No timer. The panel used to re-read the log every 30s while it was open, which
// asked the database "did anything change?" 120 times an hour to catch an
// occasional delete made on another screen. Every one of those changes starts as
// a write from this app, so the write says so (utils/dataChangeSignal) and the
// panel re-reads then — in the same tab and in other tabs of the same browser.
//
// A structural change made on a DIFFERENT device is caught when the panel is
// looked at again. That is a freshness gap, not a safety one: what may be
// reverted is decided by the server from sequence_id, and a revert of a stale
// row is refused with a message this component renders verbatim. The list being
// a minute behind has never been what makes a revert safe.

const formatDateTime = (raw) => {
  const d = new Date(raw);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${date} · ${time}`;
};

const formatDate = (raw) => {
  const d = new Date(raw);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * A revert that the server refused with a 403 does not throw.
 *
 * services/httpClient.js returns `{ success: false, error }` on 403 instead of raising, so a
 * Historical Data Security Lock refusal arrived as a *resolved* promise. The old handler took
 * the absence of a throw as success: it closed the dialog, refetched, and showed nothing at
 * all — the one refusal a user could not see. Success is therefore asserted positively.
 *
 * @returns {{ok: true} | {ok: false, body: object}}
 */
function readRevertResponse(response) {
  if (response && typeof response === 'object' && response.success === false) {
    return { ok: false, body: response };
  }
  return { ok: true };
}

/**
 * @param {object}   props
 * @param {'page'|'modal'} props.variant     which frame to draw. Behaviour is identical.
 * @param {boolean} [props.isOpen]           modal only; the page is always open.
 * @param {Function} [props.onClose]         modal only.
 * @param {Function} [props.onBack]          page only.
 * @param {Function} [props.onReverted]      called after a successful revert, so the host
 *                                           screen can re-read data a revert changed (a
 *                                           reverted merge re-creates an item; a reverted
 *                                           rename changes the ingredient list).
 * @param {boolean} [props.hideRetentionWindow] drop the "(Last 7 Days)" framing where the log
 *                                           is showing changes just made on the same screen.
 */
const StructuralActionLog = ({
  variant = 'page',
  isOpen = true,
  onClose,
  onBack,
  onReverted,
  hideRetentionWindow = false,
}) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revertingId, setRevertingId] = useState(null);
  const [error, setError] = useState('');
  const [alertMsg, setAlertMsg] = useState(null);
  const [pendingRevert, setPendingRevert] = useState(null);

  const active = variant === 'modal' ? isOpen : true;

  const fetchLogs = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await http.get('/revert-engine/logs');
      setLogs(response && Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setError('Failed to fetch structural action logs.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Read once when the panel opens.
  useEffect(() => {
    if (!active) return;
    fetchLogs(false);
  }, [active, fetchLogs]);

  // Re-read when something is actually changed — a delete or merge on another
  // screen, or in another tab — and when the panel is returned to.
  useEffect(() => {
    if (!active) return undefined;
    const stopListening = onDataChanged(() => fetchLogs(true));
    const stopResuming = onScreenResumed(() => fetchLogs(true));
    return () => {
      stopListening();
      stopResuming();
    };
  }, [active, fetchLogs]);

  const requestRevert = (log) => {
    if (log.isLocked) {
      setAlertMsg({
        heading: 'Chain-Lock Violation',
        message: 'Newer structural changes exist. You must revert them first in reverse-chronological order.',
      });
      return;
    }
    setPendingRevert(log);
  };

  const handleRevert = async (log) => {
    setPendingRevert(null);
    setRevertingId(log.log_id);
    try {
      const outcome = readRevertResponse(await http.post(`/revert-engine/revert/${log.log_id}`));
      if (!outcome.ok) {
        setAlertMsg({ heading: 'Revert Failed', message: revertErrorMessage(outcome.body) });
        return;
      }
      await fetchLogs(false);
      if (typeof onReverted === 'function') onReverted(log);
    } catch (err) {
      console.error('Failed to revert action:', err);
      setAlertMsg({ heading: 'Revert Failed', message: revertErrorMessage(err) });
    } finally {
      setRevertingId(null);
    }
  };

  // ── Shared pieces ─────────────────────────────────────────────────────────

  const dialogs = (
    <>
      {alertMsg && (
        <div className="inapp-alert-overlay">
          <div className="inapp-alert-box">
            <h3 className="inapp-alert-heading">{alertMsg.heading}</h3>
            <p className="inapp-alert-message">{alertMsg.message}</p>
            <button className="inapp-alert-ok" onClick={() => setAlertMsg(null)}>OK</button>
          </div>
        </div>
      )}

      {pendingRevert && (
        <div className="inapp-alert-overlay">
          <div className="inapp-alert-box">
            <h3 className="inapp-alert-heading">Revert this action?</h3>
            <p className="inapp-alert-message">{revertSummary(pendingRevert)}</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '6px' }}>
              <button
                className="inapp-alert-ok inapp-alert-cancel"
                onClick={() => setPendingRevert(null)}
              >
                Cancel
              </button>
              <button className="inapp-alert-ok" onClick={() => handleRevert(pendingRevert)}>
                Revert
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const emptySub = hideRetentionWindow
    ? 'No structural changes have been made yet.'
    : 'No structural changes were made in the last 7 days.';

  const body = (
    <>
      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="structural-spinner-container">
          <div className="structural-spinner"></div>
          <div className="loading-text">Loading logs...</div>
        </div>
      ) : logs.length === 0 ? (
        <div className="no-data">
          <div className="no-data-icon">📋</div>
          <p className="no-data-title">No Actions Found</p>
          <p className="no-data-sub">
            {emptySub}<br />Renames, unit changes, and merges will appear here.
          </p>
        </div>
      ) : (
        <div className="logs-list">
          {logs.map((log) => (
            <div
              key={log.log_id}
              className={`log-card ${log.reverted ? 'reverted' : ''} ${log.isLocked ? 'locked' : ''}`}
            >
              <div className="log-header">
                <span className="action-type-badge">
                  {String(log.action_type || '').replace('_', ' ').toUpperCase()}
                </span>
                <span className="log-date">{formatDateTime(log.timestamp)}</span>
              </div>

              {/* Snapshot rendering is shared too, so a change to the envelope format cannot
                  be picked up by one screen and missed by the other. */}
              <StructuralActionDetails log={log} />

              <div className="log-actions">
                {log.reverted ? (
                  <span className="status-reverted">✓ Reverted on {formatDate(log.reverted_at)}</span>
                ) : (
                  <button
                    className={`revert-btn ${log.isLocked ? 'btn-locked' : ''}`}
                    onClick={() => requestRevert(log)}
                    disabled={revertingId === log.log_id || log.reverted}
                    title={
                      log.isLocked
                        ? 'Chain-locked by a newer action. Revert newer actions first.'
                        : 'Revert this action'
                    }
                  >
                    {revertingId === log.log_id
                      ? 'Reverting...'
                      : log.isLocked
                      ? 'Chain-Locked'
                      : 'Revert Action'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  // ── Chrome ────────────────────────────────────────────────────────────────

  if (variant === 'modal') {
    if (!isOpen) return null;
    return (
      <div className="modal-overlay">
        {dialogs}
        <div className="modal-content structural-log-modal">
          <div className="modal-header">
            <h2>Structural Action Log{hideRetentionWindow ? '' : ' (Last 7 Days)'}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button className="close-btn" onClick={onClose}>&times;</button>
            </div>
          </div>
          <div className="modal-body">{body}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="irr-page">
      {dialogs}

      <header className="irr-header">
        <div className="irr-shell">
          <div className="irr-header__inner">
            <div className="irr-title-row">
              <div className="irr-header-left">
                <button onClick={onBack} className="irr-back-btn" aria-label="Back">
                  <ArrowLeft size={24} />
                </button>
                <h1>Structural Action Log</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="irr-shell irr-main">
        {body}
      </main>
    </div>
  );
};

export default StructuralActionLog;
